import styled from '@emotion/styled'
import {
  ExtranetCustomerUserRoleDto,
  ExtranetUserDto,
  UpdateCustomerUserRolesDto,
} from '@pharmac-extranet/extranet-api'
import {
  ControlledCheckboxes,
  MediaQuery,
  useBreakpoint,
  useLanguage,
} from '@pharmac-extranet/shared'
import {
  PharmacButton,
  PharmacCheckbox,
  PharmacColumn,
  PharmacContainer,
  PharmacFieldset,
  PharmacFieldsetLegend,
  PharmacH5,
  PharmacH6,
  PharmacIcon,
  PharmacSuccessToast,
  PharmacText,
  usePharmacToast,
} from '@pharmac/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { Control, FieldValues, useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import { useAuthenticationContext } from '../../features/authentication/useAuthentication'
import { useCustomerContext } from '../../features/customer/CustomerProvider'
import {
  customerQueryKeyFactory,
  getCustomerUserRolesQuery,
} from '../../features/customer/customership.queries'
import { useUserCustomerDetails } from '../../features/customer/useUserCustomerDetails'
import { useCurrentUser } from '../../features/users/useCurrentUser'
import { useExtranetPath } from '../../router/useExtranetPath'
import { api } from '../../services/HttpService'
import { ConfirmationModal } from '../confirmation-modal/ConfirmationModal'

const UserInfoContainer = styled(PharmacContainer)`
  display: flex;
  justify-content: space-between;
  width: 100%;
  padding-left: var(--spacing-medium);
  margin-top: 1rem;

  ${MediaQuery.maxTablet} {
    width: 100%;
    padding-left: var(--spacing-large);
    display: flex;
    flex-direction: column;
  }
`

const ButtonRow = styled(PharmacContainer)`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`

const ColumnContainer = styled(PharmacContainer)`
  width: 100%;
`

const InfoFormContainer = styled(PharmacContainer)`
  width: 100%;
  background-color: var(--color-accent-2);
  border-radius: 20px;
  padding: var(--spacing-medium) 0;
`

const BasicUserCheckbox = styled(PharmacCheckbox)`
  background-color: var(--color-accent-2);
  width: 1;

  &:hover {
    cursor: not-allowed;
  }
`

export const UpdateCustomerUser = () => {
  const { key, t } = useLanguage(['extranet', 'common'])
  const navigate = useNavigate()
  const { id } = useParams()
  const { customerUsers, userCustomer, customership, allRoles } = useUserCustomerDetails()
  const { isPharmacCustomerService } = useAuthenticationContext()
  const [deleteModal, setDeleteModal] = useState<boolean>(false)
  const { data: currentUser } = useCurrentUser()
  const toastHook = usePharmacToast()
  const queryClient = useQueryClient()
  const { minDesktop } = useBreakpoint()
  const { customer } = useCustomerContext()

  const [user, setUser] = useState<ExtranetUserDto>()
  const createLink = useExtranetPath()

  const { data: userRoles } = useQuery(
    getCustomerUserRolesQuery(customership?.id, user?.keycloakId)
  )

  const userRolesFormhook = useForm<UpdateCustomerUserRolesDto>({
    mode: 'onChange',
    defaultValues: {
      roleIds: [],
    },
  })

  const selectedRoleIds = userRolesFormhook.watch('roleIds')

  const filteredRolesList = (allRoles || [])
    .filter((a) => {
      if (a.role == 'customer-user') return false
      if (
        a.role === 'customer-view-orders' &&
        customer?.companyRole?.role !== 'A01' &&
        !selectedRoleIds.includes(a.id)
      ) {
        return false
      }
      return true
    })
    .map((role) => ({
      value: role.id,
      name: {
        fi: t(`customer.roles.${role.role}`),
        sv: t(`customer.roles.${role.role}`),
        en: t(`customer.roles.${role.role}`),
      },
    }))

  useEffect(() => {
    if (!customerUsers) return
    const selectedUser = customerUsers.find((a) => a.id === Number(id))

    setUser(() => {
      if (!selectedUser) return
      return selectedUser
    })
  }, [customerUsers])

  useEffect(() => {
    if (!userRoles) return
    userRolesFormhook.reset({
      roleIds: userRoles.roles.map((a) => a.id) ?? [],
    })
  }, [userRoles])

  const isEditingSelf = user?.id === currentUser?.id

  const { mutate: deleteCustomerUser } = useMutation({
    mutationFn: (user: { id: number; uid: string }) =>
      api.customers.removeCustomerUser(user.id, user.uid),
    onSuccess: async () => {
      toastHook.add({
        autoClose: true,
        autoCloseTimeout: 4000,
        toast: (
          <PharmacSuccessToast>
            {t('user.deletedSuccessfully', { ns: 'extranet' })}
          </PharmacSuccessToast>
        ),
      })
      queryClient.invalidateQueries({
        queryKey: customerQueryKeyFactory.getCustomerUsers(Number(userCustomer?.id)),
      })
    },
  })

  const handleDeleteCustomerUser = () => {
    deleteCustomerUser({ id: Number(userCustomer?.id), uid: String(user?.keycloakId) })
    setDeleteModal(false)
    navigate(createLink('oma-annos.customership'))
  }

  const { mutate: updateUserRolesInfo } = useMutation({
    mutationFn: (user: { id: number; uid: string; dto: UpdateCustomerUserRolesDto }) =>
      api.customers.updateCustomerUserRoles(user.id, user.uid, user.dto),
    onSuccess: async (response) => {
      toastHook.add({
        autoClose: true,
        autoCloseTimeout: 4000,
        toast: (
          <PharmacSuccessToast>
            {t('user.infoUpdatedSuccessfully', { ns: 'extranet' })}
          </PharmacSuccessToast>
        ),
      })

      queryClient.invalidateQueries({
        queryKey: customerQueryKeyFactory.getCustomerUserRoles(
          Number(userCustomer?.id),
          String(user?.keycloakId)
        ),
      })

      const isUserManagementRoleRemoved = !response.roles.find(
        (a: ExtranetCustomerUserRoleDto) => a.role === 'customer-user-management'
      )

      if (isEditingSelf && isUserManagementRoleRemoved) {
        navigate(createLink('root'))
      } else {
        navigate(createLink('oma-annos.customership.users'))
      }
    },
  })

  const handleUpdateUserRoles = (dto: UpdateCustomerUserRolesDto) => {
    updateUserRolesInfo({ id: Number(userCustomer?.id), uid: String(user?.keycloakId), dto })
  }

  if (!user) return null

  return (
    <InfoFormContainer>
      <PharmacFieldset marginLarge>
        <PharmacFieldsetLegend>
          <PharmacH5>
            {user.firstName} {user.lastName}
          </PharmacH5>
        </PharmacFieldsetLegend>
        <UserInfoContainer>
          <ColumnContainer>
            <PharmacColumn paddingMediumLeft>
              <PharmacH6>{t('lastName', { ns: 'common' })}</PharmacH6>
              <PharmacContainer
                marginMediumTop
                marginLargeBottom
              >
                <PharmacText>{user.lastName || '-'}</PharmacText>
              </PharmacContainer>
              <PharmacH6>{t('firstName', { ns: 'common' })}</PharmacH6>
              <PharmacContainer
                marginMediumTop
                marginLargeBottom
              >
                <PharmacText>{user.firstName || '-'}</PharmacText>
              </PharmacContainer>
              <PharmacH6>{t('userName', { ns: 'common' })}</PharmacH6>
              <PharmacContainer
                marginMediumTop
                marginLargeBottom
              >
                <PharmacText>{user.userName || '-'}</PharmacText>
              </PharmacContainer>
              <PharmacH6>{t('emailAddress', { ns: 'common' })}</PharmacH6>
              <PharmacContainer
                marginMediumTop
                marginLargeBottom
              >
                <PharmacText>{user.emailAddress || '-'}</PharmacText>
              </PharmacContainer>
              <PharmacH6>{t('phoneNumber', { ns: 'common' })}</PharmacH6>
              <PharmacContainer
                marginMediumTop
                marginLargeBottom
              >
                <PharmacText>{user.phoneNumber || '-'}</PharmacText>
              </PharmacContainer>
            </PharmacColumn>
          </ColumnContainer>

          <ColumnContainer>
            <PharmacColumn paddingMediumLeft>
              <PharmacH6>{t('streetName', { ns: 'common' })}</PharmacH6>
              <PharmacContainer
                marginMediumTop
                marginLargeBottom
              >
                <PharmacText>{user.streetName || '-'}</PharmacText>
              </PharmacContainer>
              <PharmacH6>{t('postCode', { ns: 'common' })}</PharmacH6>
              <PharmacContainer
                marginMediumTop
                marginLargeBottom
              >
                <PharmacText>{user.postCode || '-'}</PharmacText>
              </PharmacContainer>
              <PharmacH6>{t('townName', { ns: 'common' })}</PharmacH6>
              <PharmacContainer
                marginMediumTop
                marginLargeBottom
              >
                <PharmacText>{user.townName || '-'}</PharmacText>
              </PharmacContainer>
              <PharmacH6>{t('country', { ns: 'common' })}</PharmacH6>
              <PharmacContainer
                marginMediumTop
                marginLargeBottom
              >
                <PharmacText>{user.countryName || '-'}</PharmacText>
              </PharmacContainer>
              <PharmacH6>{t('certificateCardNumber', { ns: 'common' })}</PharmacH6>
              <PharmacContainer
                marginMediumTop
                marginLargeBottom
              >
                <PharmacText>{user.certificateCardNumber || '-'}</PharmacText>
              </PharmacContainer>
            </PharmacColumn>
          </ColumnContainer>

          <ColumnContainer>
            <PharmacColumn paddingMediumLeft>
              <PharmacContainer marginLargeBottom>
                <PharmacColumn>
                  <PharmacH6 marginMediumBottom>{t('common:professionalRoles')}</PharmacH6>
                  {user.professionalRoles &&
                    user.professionalRoles.map((role) => (
                      <PharmacText marginSmallTop>{key(role?.displayName)}</PharmacText>
                    ))}
                </PharmacColumn>
              </PharmacContainer>
              <PharmacContainer
                paddingLargeTop={minDesktop}
                marginLargeBottom
              >
                <PharmacColumn>
                  <PharmacH6>{t('user.userRoles', { ns: 'extranet' })}</PharmacH6>
                  <BasicUserCheckbox
                    marginSmallVertical
                    checked
                    disabled
                    handleChange={() => null}
                  >
                    {t('customer.roles.customer-user', { ns: 'extranet' })}
                  </BasicUserCheckbox>
                  <ControlledCheckboxes
                    column
                    data={filteredRolesList}
                    name="roleIds"
                    control={userRolesFormhook.control as unknown as Control<FieldValues>}
                  />
                </PharmacColumn>
              </PharmacContainer>
            </PharmacColumn>
          </ColumnContainer>
        </UserInfoContainer>
      </PharmacFieldset>

      <ButtonRow>
        <PharmacContainer paddingMediumLeft>
          <PharmacButton
            marginMediumLeft
            disabled={currentUser?.id === user.id || isPharmacCustomerService}
            onClick={() => setDeleteModal(true)}
          >
            <PharmacIcon
              size="15px"
              icon="minus"
            />
            <PharmacText marginSmallLeft>
              {t('customer.removeUser', { ns: 'extranet' })}
            </PharmacText>
          </PharmacButton>
        </PharmacContainer>
        <PharmacContainer paddingLargeRight>
          <PharmacButton
            onClick={() => navigate(createLink('oma-annos.customership.users'))}
            marginLargeRight
            variant="secondary"
          >
            {t('cancel', { ns: 'common' })}
          </PharmacButton>
          <PharmacButton
            disabled={!userRolesFormhook.formState.isDirty || isPharmacCustomerService}
            onClick={userRolesFormhook.handleSubmit(handleUpdateUserRoles)}
          >
            {t('save', { ns: 'common' })}
          </PharmacButton>
        </PharmacContainer>
      </ButtonRow>
      <ConfirmationModal
        onClose={() => setDeleteModal(false)}
        onSubmit={handleDeleteCustomerUser}
        showOnProp={deleteModal}
        title={t('extranet:customer.removeUserTitle')}
        content={t('extranet:customer.removeUserDesc')}
      />
    </InfoFormContainer>
  )
}
